// Quanto de um boleto de empresa parceira (Gran) ficou com a Vigor, com a parceira
// e com a Equatorial — a partir do que foi de fato para o Asaas, não da divisão
// econômica teórica.
//
// Por que não usar `split_valor_parceira` / `split_valor_vigor` direto: eles são a
// divisão de energia + bandeira pelo % da UC, ANTES da taxa do Asaas, e não contam
// a Equatorial embutida no boleto único. O dinheiro que andou é o `fixedValue` do
// split, gravado na emissão e conferido contra o Asaas em 01/10/2026 (bate nas 78
// faturas pagas da Gran):
//
//   - Conta da PARCEIRA emitiu (até 26/09/2026): o split vai para a Vigor
//     (`split_repasse_vigor`). A parceira fica com o resto e paga a Equatorial. Nas
//     faturas de JUL/2026 o split saiu por uma regra antiga e não é
//     `valor_boleto_vigor − split_valor_parceira`; por isso o split manda.
//   - Conta da VIGOR emitiu (desde 26/09/2026): o split vai para a parceira
//     (`split_repasse_parceira`, já sem a taxa simulada). A Vigor fica com o resto
//     e paga a Equatorial.
//
// A taxa do Asaas é debitada da conta que emitiu e não está gravada na fatura, então
// a parte de quem emitiu sai ANTES dela. Vigor + parceira + Equatorial = boleto.

export interface FaturaDivisaoInput {
    empresa_parceira_id?: string | null;
    fora_parceria_vigor?: boolean | null;
    conta_asaas_emissora?: 'vigor' | 'parceira' | string | null;
    valor_boleto_vigor?: number | string | null;
    valor_emissao_boleto_unico?: number | string | null;
    split_valor_parceira?: number | string | null;
    split_repasse_vigor?: number | string | null;
    split_repasse_parceira?: number | string | null;
}

export interface DivisaoBoletoParceira {
    /** O que o cliente pagou no boleto (com a Equatorial, quando embutida). */
    boleto: number;
    /** Parte da Vigor. */
    vigor: number;
    /** Parte da parceira. Pode sair negativa nos splits antigos de JUL/2026. */
    parceira: number;
    /** Conta de luz embutida no boleto: passa por quem emitiu, que paga a distribuidora. */
    equatorial: number;
    /** Conta Asaas em que a cobrança nasceu — é dela que sai a taxa do Asaas. */
    emissora: 'vigor' | 'parceira';
}

const num = (v: number | string | null | undefined): number => {
    const n = Number(v ?? 0);
    return Number.isFinite(n) ? n : 0;
};

const round2 = (v: number): number => Math.round(v * 100) / 100;

/**
 * `null` quando a fatura não é de parceira ou não tem divisão gravada (cancelada,
 * ou emitida antes de 03/08/2026, quando o split ainda não era registrado).
 */
export function divisaoBoletoParceira(f: FaturaDivisaoInput): DivisaoBoletoParceira | null {
    if (!f.empresa_parceira_id) return null;
    if (f.split_valor_parceira == null && f.split_repasse_vigor == null && f.split_repasse_parceira == null) return null;

    const emissora = f.conta_asaas_emissora === 'vigor' ? 'vigor' : 'parceira';
    const boleto = round2(num(f.valor_emissao_boleto_unico ?? f.valor_boleto_vigor));

    // Cliente próprio da parceira: sem split, a Vigor não tem parte. A parte da
    // parceira gravada na emissão já é boleto − Equatorial (calcularCobrancaForaParceria).
    if (f.fora_parceria_vigor) {
        const parceira = round2(num(f.split_valor_parceira));
        return { boleto, vigor: 0, parceira, equatorial: round2(boleto - parceira), emissora };
    }

    const semEquatorial = round2(num(f.valor_boleto_vigor));
    const equatorial = round2(Math.max(0, boleto - semEquatorial));

    if (emissora === 'parceira') {
        const vigor = round2(f.split_repasse_vigor != null
            ? num(f.split_repasse_vigor)
            : semEquatorial - num(f.split_valor_parceira));
        return { boleto, vigor, parceira: round2(semEquatorial - vigor), equatorial, emissora };
    }

    const parceira = round2(num(f.split_repasse_parceira));
    return { boleto, vigor: round2(semEquatorial - parceira), parceira, equatorial, emissora };
}
